import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { ThemeProvider } from '@/providers/ThemeProvider'
import { AuthProvider } from '@/contexts/AuthContext'
import { HeaderProvider } from '@/contexts/HeaderContext'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { useAppBranding } from '@/hooks/useAppBranding'

// Páginas
import Login from '@/pages/Login'
import Dashboard from '@/pages/Dashboard'
import BdrPanel from '@/pages/BdrPanel'
import Deals from '@/pages/Deals'
import CloserLeads from '@/pages/CloserLeads'
import Empresas from '@/pages/Empresas'
import Partners from '@/pages/Partners'
import PartnerPromotions from '@/pages/PartnerPromotions'
import RocketSearch from '@/pages/RocketSearch'
import Tarefas from '@/pages/Tarefas'
import { AuthBootstrap } from '@/components/AuthBootstrap'
import { HomeRoute } from '@/components/HomeRoute'
import ProposalStudioPage from '@/pages/ProposalStudioPage'
import Financeiro from '@/pages/Financeiro'
import FinanceRequestDetailPage from '@/pages/FinanceRequestDetailPage'
import FinanceiroSolicitacoes from '@/pages/FinanceiroSolicitacoes'
import PortalPj from '@/pages/PortalPj'
import ClientesAgrada from '@/pages/ClientesAgrada'
import ClienteAgradaCadastroPage from '@/pages/ClienteAgradaCadastroPage'
import PjAgradaPage from '@/pages/PjAgrada'
import { AppClosingRoutes } from '@/routes/AppClosingRoutes'
import ClienteAgradaDetailPage from '@/pages/ClienteAgradaDetailPage'
import CsDashboardPage from '@/pages/CsDashboardPage'
import CsCarteiraPage from '@/pages/CsCarteiraPage'
import CsRetencaoPage from '@/pages/CsRetencaoPage'
import CsAccountDetailPage from '@/pages/CsAccountDetailPage'
import Gestores from '@/pages/Gestores'
import GestorEntradaDetailPage from '@/pages/GestorEntradaDetailPage'
import GestorEquipeDetailPage from '@/pages/GestorEquipeDetailPage'
import GestorDashboardTaskDetailPage from '@/pages/GestorDashboardTaskDetailPage'
import { BlocosShell } from '@/components/Blocos/BlocosShell'
import { PwaUpdateNotifier } from '@/components/PwaUpdateNotifier'
import { isRocketInstance } from '@/config/instanceProfile'

export default function App() {
  useAppBranding()

  return (
    <ThemeProvider>
      <AuthProvider>
        <AuthBootstrap>
        <HeaderProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            {/* Rota pública */}
            <Route path='/login' element={<Login />} />

            {/* Rotas protegidas */}
            <Route path='/' element={<HomeRoute />} />
            <Route
              path='/dashboard'
              element={
                <ProtectedRoute
                  requiredRole={
                    isRocketInstance ? ['admin', 'closer', 'manager'] : ['admin', 'closer']
                  }
                >
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path='/bdr-panel'
              element={
                <ProtectedRoute requiredRole={['admin', 'bdr', 'closer']}>
                  <BdrPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path='/deals'
              element={
                <ProtectedRoute
                  requiredRole={
                    isRocketInstance
                      ? ['admin', 'closer', 'bdr', 'manager']
                      : ['admin', 'closer', 'bdr']
                  }
                >
                  <Deals />
                </ProtectedRoute>
              }
            />
            <Route
              path='/deals-v2'
              element={<Navigate to='/deals' replace />}
            />
            <Route
              path='/tarefas'
              element={
                <ProtectedRoute requiredRole={['admin', 'manager', 'closer', 'bdr', 'cs']}>
                  <Tarefas />
                </ProtectedRoute>
              }
            />
            <Route
              path='/closer-leads'
              element={
                <ProtectedRoute
                  requiredRole={
                    isRocketInstance
                      ? ['admin', 'closer', 'bdr', 'manager']
                      : ['admin', 'closer', 'bdr']
                  }
                >
                  <CloserLeads />
                </ProtectedRoute>
              }
            />
            <Route
              path='/empresas'
              element={
                <ProtectedRoute
                  requiredRole={
                    isRocketInstance
                      ? ['admin', 'closer', 'bdr', 'manager']
                      : ['admin', 'closer', 'bdr']
                  }
                >
                  <Empresas />
                </ProtectedRoute>
              }
            />
            <Route
              path='/partners'
              element={
                <ProtectedRoute
                  requiredRole={
                    isRocketInstance ? ['admin', 'closer', 'manager'] : ['admin', 'closer']
                  }
                >
                  <Partners />
                </ProtectedRoute>
              }
            />
            <Route
              path='/partners/promocoes'
              element={
                <ProtectedRoute requiredRole={['admin', 'closer', 'manager']}>
                  <PartnerPromotions />
                </ProtectedRoute>
              }
            />
            <Route
              path='/rocket-search'
              element={
                <ProtectedRoute requiredRole={['admin', 'bdr', 'closer']}>
                  <RocketSearch />
                </ProtectedRoute>
              }
            />
            <Route
              path='/proposal-studio'
              element={
                <ProtectedRoute requiredRole={['admin', 'closer', 'bdr']}>
                  <ProposalStudioPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/clientes/novo'
              element={
                <ProtectedRoute requiredRole={['admin', 'financeiro']}>
                  <ClienteAgradaCadastroPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/clientes/:id'
              element={
                <ProtectedRoute requiredRole={['admin', 'financeiro', 'closer']}>
                  <ClienteAgradaDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/clientes'
              element={
                <ProtectedRoute requiredRole={['admin', 'financeiro', 'closer']}>
                  <ClientesAgrada />
                </ProtectedRoute>
              }
            />
            <Route
              path='/pj-agrada'
              element={
                <ProtectedRoute requiredRole={['admin', 'financeiro']}>
                  <PjAgradaPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/gestores/chamados/:requestId'
              element={
                <ProtectedRoute requiredRole={['admin', 'manager']}>
                  <FinanceRequestDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/gestores/equipe/:memberId'
              element={
                <ProtectedRoute requiredRole={['admin', 'manager']}>
                  <GestorEquipeDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/gestores/entradas/:entradaId'
              element={
                <ProtectedRoute requiredRole={['admin', 'manager']}>
                  <GestorEntradaDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/gestores/dashboard/tarefa/:taskId'
              element={
                <ProtectedRoute requiredRole={['admin', 'manager']}>
                  <GestorDashboardTaskDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/gestores'
              element={
                <ProtectedRoute requiredRole={['admin', 'manager']}>
                  <Gestores />
                </ProtectedRoute>
              }
            />
            <Route
              path='/portal'
              element={
                <ProtectedRoute
                  requiredRole={[
                    'admin',
                    'manager',
                    'closer',
                    'bdr',
                    'financeiro',
                    'usuario_agrada',
                  ]}
                >
                  <PortalPj />
                </ProtectedRoute>
              }
            />
            <Route path='/portal-pj' element={<Navigate to='/portal' replace />} />
            <Route
              path='/financeiro/solicitacoes'
              element={
                <ProtectedRoute
                  requiredRole={['admin', 'financeiro', 'closer', 'bdr']}
                >
                  <FinanceiroSolicitacoes />
                </ProtectedRoute>
              }
            />
            <Route
              path='/financeiro/fila/:requestId'
              element={
                <ProtectedRoute requiredRole={['admin', 'financeiro']}>
                  <FinanceRequestDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/financeiro'
              element={
                <ProtectedRoute requiredRole={['admin', 'financeiro']}>
                  <Financeiro />
                </ProtectedRoute>
              }
            />
            <Route
              path='/cs/contas/:id'
              element={
                <ProtectedRoute requiredRole={['admin', 'manager', 'cs']}>
                  <CsAccountDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/cs/carteira'
              element={
                <ProtectedRoute requiredRole={['admin', 'manager', 'cs']}>
                  <CsCarteiraPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/cs/retencao'
              element={
                <ProtectedRoute requiredRole={['admin', 'manager', 'cs']}>
                  <CsRetencaoPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/cs'
              element={
                <ProtectedRoute requiredRole={['admin', 'manager', 'cs']}>
                  <CsDashboardPage />
                </ProtectedRoute>
              }
            />
            {AppClosingRoutes()}
          </Routes>
          <BlocosShell />
        </BrowserRouter>
        </HeaderProvider>
        </AuthBootstrap>
        <PwaUpdateNotifier />
        <Toaster
          position='top-right'
          offset={16}
          style={{ zIndex: 99999 }}
          toastOptions={{
            style: {
              background: 'var(--bg-card)',
              border: '1px solid var(--glass-border)',
              color: 'var(--text-primary)',
            },
          }}
        />
      </AuthProvider>
    </ThemeProvider>
  )
}
